'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Heading,
  Show,
  Text,
  useRadioGroup,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import LinkCustom from '@/app/_components/link/LinkCustom';
import { ReactComponent as ArrowBackwardCircleOutline } from '@/app/_icons/ArrowBackwardCircleOutline.svg';
import { ReactComponent as ArrowForwardCircleOutline } from '@/app/_icons/ArrowForwardCircleOutline.svg';
import { ReactComponent as CkGreyLeftArrow } from '@/app/_icons/CkGreyLeftArrow.svg';
import { ReactComponent as CkGreyRightArrow } from '@/app/_icons/CkGreyRightArrow.svg';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { NewCarData } from '@/types/new-cars/new-cars.types';

import ModelCard from './ModelCard';
import Styles from './ModelSelectionPage.module.scss';

type Props = {
  newVehicles: NewCarData;
  selectedModelMmcode?: string;
  basePath: '/build-price' | '/buy-online';
};

export const BuildAndBuyModelSelectionPage = ({
  newVehicles,
  selectedModelMmcode,
  basePath = '/build-price',
}: Props) => {
  const swiperRef: React.Ref<SwiperRef> = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedModel, setSelectedModel] = useState(
    selectedModelMmcode ?? (newVehicles && newVehicles?.specification_prices[0])
      ? newVehicles?.specification_prices[0]?.mm_code
      : '',
  );

  const initialRenderRef = useRef(true);

  const { setFormItem } = useFloatingCTA();

  useEffect(() => {
    setFormItem({
      type: 'new-car',
      newCar: newVehicles,
      mmCode: selectedModel,
      source:
        basePath === '/build-price'
          ? newVehicles?.source_bp
          : newVehicles?.source_bo,
    });

    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      if (!selectedModelMmcode && selectedModel) {
        updateUrlParam('mmcode', selectedModel);
      }
    }
    return () => {
      initialRenderRef.current = true;
      setFormItem(undefined);
    };
  }, [
    selectedModel,
    selectedModelMmcode,
    newVehicles,
    selectedModel,
    setFormItem,
  ]);

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const CustomNavigation = () => {
    return (
      <div className="swiper-navigation">
        <button
          onClick={goPrev}
          className="swiper-button-prev-2"
          role="button"
          name="Previous"
        >
          <Show breakpoint="(min-width: 768px)">
            <CkGreyLeftArrow role="button" name="Previous" />
          </Show>
          <Show breakpoint="(max-width: 767px)">
            <ArrowBackwardCircleOutline role="button" name="Previous" />
          </Show>
        </button>
        <button
          onClick={goNext}
          className="swiper-button-next-2"
          role="button"
          name="Next"
        >
          <Show breakpoint="(min-width: 768px)">
            <CkGreyRightArrow role="button" name="Next" />
          </Show>
          <Show breakpoint="(max-width: 767px)">
            <ArrowForwardCircleOutline role="button" name="Next" />
          </Show>
        </button>
      </div>
    );
  };

  const handleChange = (value: string) => {
    setSelectedModel(value);
    updateUrlParam('mmcode', value);
  };

  const updateUrlParam = (param: string, value: string) => {
    const newSearchParams = new URLSearchParams(window.location.search);

    newSearchParams.set(param, value);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${newSearchParams.toString()}`,
    );
  };

  const options = newVehicles?.specification_prices.map(
    (model) => model.mm_code,
  );

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'model',
    defaultValue: selectedModelMmcode ?? options[0],
    onChange: handleChange,
  });
  const group = getRootProps();

  return (
    <Box
      paddingTop={{ base: '100px', lg: '134px' }}
      paddingBottom={'0px'}
      className={Styles.modelSelectionWrapper}
    >
      <Container
        maxWidth={{ base: '100%', md: '1328px' }}
        alignContent={'center'}
        position={'relative'}
      >
        <Box className={Styles.headingElement} marginBottom={'40px'}>
          <Heading
            as="h1"
            className={Styles.titleLg}
            textAlign={'center'}
            marginBottom={{ base: 0, md: 8 }}
          >
            {newVehicles?.main_title} Models
          </Heading>

          <Text textAlign={'center'}>
            (<strong>{newVehicles?.specification_prices?.length}</strong>{' '}
            variants available)
          </Text>
        </Box>
        <Box className={Styles.modelWrapper} {...group}>
          <Box className={Styles.modelCarouselContainer}>
            <Swiper
              className={Styles.modelCarousel}
              ref={swiperRef}
              slidesPerView={3}
              spaceBetween={0}
              initialSlide={
                newVehicles?.specification_prices?.findIndex(
                  (speCar) => speCar?.mm_code === selectedModelMmcode,
                ) || 0
              }
              modules={[Pagination, Navigation]}
              pagination={{
                el: '.swiper-pagination',
                type: 'fraction',
                formatFractionCurrent: function (number) {
                  return '0' + number;
                },
              }}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              onSlideChange={(swiper) => {
                setCurrentPage(swiper.realIndex + 1);
              }}
              onSwiper={(swiper) => {
                setTotalPages(swiper.slides.length);
              }}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                  spaceBetween: '0',
                },
                992: {
                  slidesPerView: 2,
                  spaceBetween: '60px',
                },
                1300: {
                  slidesPerView: 3,
                  spaceBetween: '80px',
                },
              }}
            >
              {newVehicles?.specification_prices?.map((speCar) => {
                const radio = getRadioProps({
                  value: speCar?.mm_code,
                });

                return (
                  <SwiperSlide
                    key={speCar?.duoporta_model_id}
                    className={Styles.swiperSlide}
                  >
                    <ModelCard
                      modelDetails={speCar}
                      value={speCar?.mm_code}
                      title={speCar?.title}
                      imageSrc={speCar?.image_id}
                      modelName={newVehicles?.main_title}
                      imageAlt={
                        newVehicles?.image_alt_tag ?? newVehicles?.main_title
                      }
                      {...radio}
                    >
                      {speCar?.mm_code}
                    </ModelCard>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <Box className={Styles.modelSwiperControls}>
              <Box className="swiper-pagination">
                <span className="current-page">{currentPage}</span>
                <span className="separator">/</span>
                <span className="total-pages">{totalPages}</span>
              </Box>
              <CustomNavigation />
            </Box>
          </Box>

          <Box className={Styles.modalMobileContainer}>
            {newVehicles?.specification_prices?.map((speCar) => {
              const radio = getRadioProps({ value: speCar?.mm_code });

              return (
                <Box
                  key={speCar?.duoporta_model_id}
                  className={Styles.swiperSlide}
                >
                  <ModelCard
                    modelDetails={speCar}
                    value={speCar?.mm_code}
                    title={speCar?.title}
                    imageSrc={newVehicles?.image_no_background_left_angle}
                    modelName={newVehicles?.main_title}
                    imageAlt={
                      newVehicles?.image_alt_tag ?? newVehicles?.main_title
                    }
                    {...radio}
                  >
                    {speCar?.mm_code}
                  </ModelCard>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box className={Styles.modelFooter}>
          <LinkCustom
            extraClass={Styles.ctaBtn}
            href={`${basePath}/${newVehicles?.slug}/${selectedModel}`}
          >
            Next <ChevronRightIcon />
          </LinkCustom>
        </Box>
      </Container>
    </Box>
  );
};
